import config from '../utils/config';
import { getAuthToken } from '../utils/auth';

/**
 * Generic API client for making HTTP requests with JWT authentication
 */
class ApiClient {
  private baseUrl: string;
  
  constructor(baseUrl: string = config.apiUrl) {
    this.baseUrl = baseUrl;
  }

  /**
   * Add authorization header with JWT token if available
   */
  private getAuthHeaders(): Record<string, string> {
    // Try to get the token from localStorage
    const token = getAuthToken();
    
    // Log the token being used for requests (partial for security)
    if (token) {
      console.log(`Using auth token: ${token.substring(0, 10)}...${token.substring(token.length - 10)}`);
    }
    
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }

  private async request<T>(
    url: string, 
    method: string = 'GET', 
    data?: any,
    headers: Record<string, string> = {}
  ): Promise<T> {
    const endpoint = `${this.baseUrl}${url}`;
    
    // Get auth token and log if it exists (without showing full token)
    const token = getAuthToken();
    if (token) {
      console.log(`Making authenticated request to ${endpoint} with token: ${token.substring(0, 10)}...`);
    } else {
      console.warn(`Making unauthenticated request to ${endpoint} - no token found`);
    }
    
    // Merge auth headers with custom headers
    const mergedHeaders = {
      'Content-Type': 'application/json',
      ...this.getAuthHeaders(),
      ...headers,
    };
    
    // Create fetch options without credentials for cross-origin requests
    const options: RequestInit = {
      method,
      headers: mergedHeaders,
      // 'omit' is safer for CORS requests than 'include' or even 'same-origin'
      // since we're using JWT bearer tokens, not cookies
      credentials: 'omit',
      mode: 'cors',
    };

    if (data) {
      options.body = JSON.stringify(data);
    }

    try {
      console.log(`Sending ${method} request to ${endpoint}`);
      const response = await fetch(endpoint, options);
      console.log(`Received response: ${response.status} ${response.statusText}`);
      
      // Log response headers for debugging
      const responseHeaders: Record<string, string> = {};
      response.headers.forEach((value, key) => {
        responseHeaders[key] = value;
      });
      console.log('Response headers:', responseHeaders);
      
      // Handle non-JSON responses
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.indexOf('application/json') !== -1) {
        const json = await response.json();
        console.log('Response body:', json);
        
        if (!response.ok) {
          // Handle auth errors specifically
          if (response.status === 401) {
            console.error('Authentication failed - invalid or expired token');
            throw new Error(`Authentication failed (401): ${json.message || 'Invalid or expired token'}`);
          }
          
          throw new Error(json.message || `API error: ${response.status} ${response.statusText}`);
        }
        
        return json;
      } else {
        if (!response.ok) {
          throw new Error(`Network error: ${response.status} ${response.statusText}`);
        }
        
        // For non-JSON responses like file downloads
        return response as unknown as T;
      }
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // HTTP method wrappers
  public async get<T>(url: string, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(url, 'GET', undefined, headers);
  }

  public async post<T>(url: string, data: any, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(url, 'POST', data, headers);
  }

  public async put<T>(url: string, data: any, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(url, 'PUT', data, headers);
  }

  public async delete<T>(url: string, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(url, 'DELETE', undefined, headers);
  }
}

export default new ApiClient();